import React, { useState, useRef } from "react";
import "./Card.scss";

const Card = ({ name, description, preview, videoPreview, githubLink, liveLink, supportLink, theme }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const hasMultipleMedia = preview && videoPreview;
  const totalSlides = hasMultipleMedia ? 2 : 1;

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!hasMultipleMedia) return;

    const swipeDistance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (swipeDistance > minSwipeDistance) {
      // Swipe left - next slide
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    } else if (swipeDistance < -minSwipeDistance) {
      // Swipe right - previous slide
      setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    }
  };

  // Mouse drag support for desktop
  const handleMouseDown = (e) => {
    setIsDragging(true);
    touchStartX.current = e.clientX;
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    touchEndX.current = e.clientX;
  };

  const handleMouseUp = () => {
    if (!isDragging || !hasMultipleMedia) {
      setIsDragging(false);
      return;
    }

    const swipeDistance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (swipeDistance > minSwipeDistance) {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    } else if (swipeDistance < -minSwipeDistance) {
      setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    }

    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Check if videoPreview is actually a video or an image
  const isVideoFile = (url) => {
    if (!url) return false;
    return url.match(/\.(mp4|webm|ogg|mov)$/i);
  };

  const renderMedia = () => {
    if (!hasMultipleMedia) {
      // Single media (either image or video)
      if (videoPreview) {
        if (isVideoFile(videoPreview)) {
          return (
            <video
              className="preview-video"
              src={videoPreview}
              autoPlay
              muted
              loop
              playsInline
            >
              Your browser does not support the video tag.
            </video>
          );
        } else {
          return (
            <img
              className="preview-image"
              src={videoPreview}
              alt={`${name} preview`}
            ></img>
          );
        }
      }
      return (
        <img
          className="preview-image"
          src={preview}
          alt={`${name} preview`}
        ></img>
      );
    }

    // Multiple media - carousel
    return (
      <div
        className="media-carousel"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        {/* Left Arrow - only show if not on first slide */}
        {currentSlide > 0 && (
          <button
            className="carousel-arrow carousel-arrow-left"
            onClick={(e) => {
              e.stopPropagation();
              setCurrentSlide((prev) => prev - 1);
            }}
            aria-label="Previous slide"
          >
            ‹
          </button>
        )}

        {/* Right Arrow - only show if not on last slide */}
        {currentSlide < totalSlides - 1 && (
          <button
            className="carousel-arrow carousel-arrow-right"
            onClick={(e) => {
              e.stopPropagation();
              setCurrentSlide((prev) => prev + 1);
            }}
            aria-label="Next slide"
          >
            ›
          </button>
        )}

        <div
          className="media-slider"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          <div className="media-slide">
            <img
              className="preview-image"
              src={preview}
              alt={`${name} preview`}
              draggable="false"
            ></img>
          </div>
          <div className="media-slide">
            {isVideoFile(videoPreview) ? (
              <video
                className="preview-video"
                src={videoPreview}
                autoPlay
                muted
                loop
                playsInline
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                className="preview-image"
                src={videoPreview}
                alt={`${name} preview 2`}
                draggable="false"
              ></img>
            )}
          </div>
        </div>
        <div className="carousel-indicators">
          {[...Array(totalSlides)].map((_, index) => (
            <button
              key={index}
              className={`indicator-dot ${currentSlide === index ? "active" : ""}`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <article
      className="project-cards"
      style={{ backgroundColor: theme === "dark" ? "#363537" : "white" }}
    >
      <h2>{name}</h2>
      <p>{description}</p>
      {renderMedia()}
      <div className="card-footer">
        <div className="footer-left">
          {supportLink && (
            <a
              href={supportLink}
              target="_blank"
              rel="noreferrer"
              className="support-link"
            >
              support
            </a>
          )}
          {githubLink && (
            <a href={githubLink} target="_blank" rel="noreferrer">
              github link
            </a>
          )}
        </div>
        <div className="footer-right">
          {liveLink && (
            <a href={liveLink} target="_blank" rel="noreferrer">
              view live
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

export default Card;
